import { jsx as _jsx } from "react/jsx-runtime";
// GridCanvas.tsx — renders the editor board on a single <canvas> using the game's board sprites, and maps
// clicks/hover back to grid coordinates. Redraws whenever the template / selection / preview changes.
import { Component, createRef } from "react";
import { drawEditorCell, drawSlot, drawConnectorBar, drawMetalRect, drawGateWide, drawPipe, drawPropellerTie, drawBolt, drawIceCover, drawMetalBox, drawLaserGate, drawLaserKey, drawSlidingDoor, drawSuperMysteryMark, drawElevator, drawGlassPanel, } from "../art/cellArt";
import { drawElevatorPatchSprite } from "../art/itemSprites";
import { onSpritesLoaded } from "../art/sprites";
import { BOARD_SIZE, MAX_TUNNEL_SPAWNS, findCell, pipeRun, propellerTiedCell, spawnerCount, spawnerOf, spawnerSpawnAt, tunnelSpawnAt } from "./templateOps";
import { glassPanelGoals, runCells } from "./sealOps";
import { CellModifierType, LASER_PAIR_COLORS } from "../Models/CellModifier";
// Cell size (device-independent px). Larger cells for a prominent, easy-to-click board (the grid scrolls
// when it doesn't fit).
function cellSizeFor(columns) {
    return Math.max(48, Math.min(96, Math.floor(700 / Math.max(1, columns))));
}
// How opaque the board draws the items laid OVER boxes (ice, metal box, lock panel, bolt, sliding door). In
// the game they hide the box; in the editor the colour under them is what the author is checking, so they
// are see-through here. Their tags and counters are drawn at full opacity regardless (drawTag, drawCounter).
export const COVER_ALPHA = 0.45;
function withCoverAlpha(ctx, draw) {
    ctx.save();
    ctx.globalAlpha = COVER_ALPHA;
    draw();
    ctx.restore();
}
// A stable, distinct-ish colour per group index for the corner badge.
function groupHue(group) {
    return `hsl(${(group * 67) % 360} 70% 55%)`;
}
// How many spawn badges a tunnel puts on one row before wrapping onto the next. Five keeps the pips big
// enough to read at the smallest cell size the board uses.
const BADGES_PER_ROW = 5;
// One group pip: a filled circle in the group's colour with the number in it, centred on (cx, cy).
function drawGroupBadge(ctx, cx, cy, r, group) {
    const label = String(group);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = groupHue(group);
    ctx.fill();
    ctx.lineWidth = Math.max(1, r * 0.125);
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.stroke();
    ctx.fillStyle = "#fff";
    // A two-digit group needs the smaller face to stay inside the pip.
    ctx.font = `bold ${Math.round(r * (label.length > 1 ? 0.9 : 1.15))}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, cx, cy + r * 0.125);
}
// Where a tunnel's spawn pips go inside a `cs`-wide cell: a centred strip across the top, wrapping onto a
// second row instead of shrinking the pips into illegibility. The pip size is fixed by BADGES_PER_ROW rather
// than by how many spawns this tunnel happens to have, so a 3-spawn and a 10-spawn tunnel read as the same
// kind of mark. Rows are balanced (7 spawns is 4+3, not 5+2) so a strip never ends in a lone pip.
export function spawnBadgeSlots(n, cs) {
    if (n <= 0) {
        return [];
    }
    const pad = cs * 0.02;
    const slot = (cs - 2 * pad) / BADGES_PER_ROW;
    const r = slot * 0.44;
    const rows = Math.ceil(n / BADGES_PER_ROW);
    const perRow = Math.ceil(n / rows);
    const out = [];
    for (let i = 0; i < n; i++) {
        const row = Math.floor(i / perRow);
        const inRow = Math.min(perRow, n - row * perRow);
        const x0 = (cs - inRow * slot) / 2;
        out.push({ cx: x0 + slot * (i - row * perRow + 0.5), cy: pad + r + row * r * 2.3, r });
    }
    return out;
}
// The cells a straight line from `from` to `to` runs through, excluding `from` and including `to`. A
// mousemove only reports where the cursor IS, so a quick stroke jumps whole cells between two samples —
// and a paint stroke promises to fill every empty cell it crosses. Stepping along the longer axis and
// rounding the other is all a stroke needs.
function cellsBetween(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    const out = [];
    for (let i = 1; i <= steps; i++) {
        out.push({ x: from.x + Math.round((dx * i) / steps), y: from.y + Math.round((dy * i) / steps) });
    }
    return out;
}
export class GridCanvas extends Component {
    constructor() {
        super(...arguments);
        this.canvasRef = createRef();
        // Left-drag gesture state (kept off React state; the actual item move lives in the parent, which
        // re-renders this canvas with the moved template each step).
        this.pointerDownCell = null;
        // Which gesture the current left-drag is: moving the item under the cursor, or painting new cells
        // across empty ones. Decided the moment the pointer leaves the pressed cell.
        this.gesture = null;
        // Whether ⇧ was down at the press: a ⇧-drag box-selects and a ⇧-click toggles the selection.
        this.pointerDownShift = false;
        // The last cell a paint stroke actually reached, so the gap to the next sample can be filled in.
        this.lastPaintCell = null;
        // Unsubscribes the sprite-load redraw (see componentDidMount).
        this.stopSpriteRedraw = null;
        // Fires for a release anywhere in the window. For one over the canvas, React's own onMouseUp (delegated
        // at the root container, which sits below window) has already run and reset the gesture, so this is a
        // no-op — `endGesture` checks `this.gesture`. No click is emitted from here: a release that did not land
        // on the cell it started on was never a click.
        this.onWindowUp = (e) => {
            if (e.button !== 0) {
                return;
            }
            this.endGesture();
            this.resetGesture();
        };
        this.onWindowBlur = () => {
            this.endGesture();
            this.resetGesture();
        };
        this.cell = 0;
        // Left-button press: remember the cell; a live drag begins once the pointer moves to another cell.
        this.onDown = (e) => {
            if (e.button !== 0) {
                return; // right-click is handled by onContextMenu
            }
            this.pointerDownCell = this.toCell(e);
            this.pointerDownShift = e.shiftKey;
            this.gesture = null;
        };
        this.onContext = (e) => {
            e.preventDefault(); // suppress the browser context menu
            this.endGesture();
            this.resetGesture();
            const cell = this.toCell(e);
            if (cell) {
                this.props.onCellRightClick?.(cell.x, cell.y, e.clientX, e.clientY);
            }
        };
        this.onMove = (e) => {
            const cell = this.toCell(e);
            this.props.onCellHover?.(cell);
            const down = this.pointerDownCell;
            if (!down) {
                this.updateCursor(cell, e.shiftKey);
                return;
            }
            if (!(e.buttons & 1)) {
                // the button was released off-canvas — end any gesture and abandon it
                this.endGesture();
                this.resetGesture();
                return;
            }
            // Begin the gesture once the pointer leaves the pressed cell. ⇧ box-selects. With the Select tool a
            // drag from inside the selection moves it and a drag from anywhere else box-selects — boards are
            // mostly items, so box-select cannot wait for an empty cell to start from. With the other tools an
            // item under the pointer is dragged (with the whole selection, when it is part of one) and an empty
            // cell paints.
            if (!this.gesture && cell && (cell.x !== down.x || cell.y !== down.y)) {
                const inSelection = this.props.selection?.has(`${down.x},${down.y}`) ?? false;
                const moves = this.props.selectMode ? inSelection : !this.props.dragPaints && findCell(this.props.template, down.x, down.y) !== undefined;
                if (this.pointerDownShift || (this.props.selectMode && !moves)) {
                    this.gesture = "marquee";
                    this.props.onMarqueeStart?.(down, this.pointerDownShift);
                }
                else if (moves) {
                    this.gesture = "drag";
                    this.props.onCellDragStart?.(down);
                }
                else {
                    this.gesture = "paint";
                    this.lastPaintCell = down;
                    this.props.onCellPaintStart?.(down);
                }
            }
            if (cell && this.gesture === "drag") {
                this.props.onCellDragOver?.(cell);
            }
            else if (cell && this.gesture === "marquee") {
                this.props.onMarqueeOver?.(cell);
            }
            else if (cell && this.gesture === "paint") {
                for (const p of cellsBetween(this.lastPaintCell ?? cell, cell)) {
                    this.props.onCellPaintOver?.(p);
                }
                this.lastPaintCell = cell;
            }
        };
        this.onUp = (e) => {
            if (e.button !== 0) {
                return;
            }
            const had = this.gesture;
            const down = this.pointerDownCell;
            const shift = this.pointerDownShift || e.shiftKey;
            const cell = this.toCell(e);
            this.endGesture();
            this.resetGesture();
            // A click is a press AND a release on the same cell of this canvas. Without checking the press
            // half, any release that merely happened to land over the board counted as one — a drag begun on
            // the toolbar and let go here, or the left button released after a right-click cancelled a live
            // drag. Since a left-click on an empty cell now places the active mechanic, both authored a cell.
            if (!had && cell && down && down.x === cell.x && down.y === cell.y) {
                this.props.onCellClick(cell.x, cell.y, shift); // no movement -> a plain click (select)
            }
        };
        // The canvas is exactly the grid, so a stroke that runs along an edge crosses out of it routinely. Ending
        // the gesture on every leave made that a dead end: `pointerDownCell` was cleared, so coming back with the
        // button still down hit onMove's `if (!down) return` and nothing more was painted until the author
        // released and pressed again — with the stroke already split into two undo entries.
        //
        // While the button is held the gesture is still the author's, so only the hover is dropped. A release
        // that happens off-canvas is not missed: onMove's `!(e.buttons & 1)` branch ends and abandons the
        // gesture on the first move back over the board.
        this.onLeave = (e) => {
            this.props.onCellHover?.(null);
            if (e.buttons & 1) {
                return;
            }
            this.endGesture();
            this.resetGesture();
        };
    }
    componentDidMount() {
        this.draw();
        // The board art is the game's sprites, decoded asynchronously; the first draws fall back to the
        // procedural art, so draw again once they are in.
        this.stopSpriteRedraw = onSpritesLoaded(() => this.draw());
        // A gesture must end wherever the button is released, not only over the canvas. The canvas is
        // exactly the grid, so running off its edge mid-stroke is routine, and onLeave deliberately keeps
        // the gesture alive while the button is held so a stroke can follow that edge. Nothing else was
        // listening for the release: a button let go over the toolbar left `gesture` and `pointerDownCell`
        // set, which meant the stroke's undo entry was never pushed, the page swallowed EVERY keyboard
        // shortcut (it ignores them while a gesture is in progress — Escape included), a later press
        // anywhere could resume the abandoned gesture, and when the pointer finally came back over the
        // board the gesture ended THEN: committing its history entry on top of unrelated edits, or putting
        // the pre-drag board back over them.
        window.addEventListener("mouseup", this.onWindowUp);
        // Same hole, different exit: alt-tab away with the button down and the release lands in another app.
        window.addEventListener("blur", this.onWindowBlur);
    }
    componentWillUnmount() {
        this.stopSpriteRedraw?.();
        window.removeEventListener("mouseup", this.onWindowUp);
        window.removeEventListener("blur", this.onWindowBlur);
    }
    componentDidUpdate() {
        this.draw();
    }
    draw() {
        const canvas = this.canvasRef.current;
        if (!canvas) {
            return;
        }
        const { template, selected, previewColors, previewSplits, previewInners, paintedCells } = this.props;
        // Bounded, because these come off the FILE and the empty-slot loop below is rows×cols however few
        // cells the board holds: a template claiming 100000×100000 asked for 10^10 draws on the first frame,
        // i.e. the tab was gone before the Error beside it could be read. validateTemplate raises that Error
        // and holds Save shut; this is what keeps the editor alive long enough to show it.
        const cols = Math.max(1, Math.min(BOARD_SIZE, template.columns));
        const rows = Math.max(1, Math.min(BOARD_SIZE, template.rows));
        const cs = cellSizeFor(cols);
        this.cell = cs;
        const dpr = window.devicePixelRatio || 1;
        const w = cols * cs;
        const h = rows * cs;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        // Empty slots first (the board grid).
        for (let y = 0; y < rows; y++) {
            for (let x = 0; x < cols; x++) {
                ctx.save();
                ctx.translate(x * cs, y * cs);
                drawSlot(ctx, cs);
                ctx.restore();
            }
        }
        // Pipes, before the cells: each runs from its head up the column above it (the empty cells the game
        // clears for its body) to its collar, so it is drawn on the board, not inside one cell. The head's own
        // cell is skipped by the cell pass below except for its spawn badges, which go over the head.
        for (const c of template.cells.items) {
            if (c && c.isPipe) {
                drawPipe(ctx, cs, c, pipeRun(template, c.x, c.y).top, Math.max(0, Math.floor(c.pipeSpawnCount ?? 0) || 0));
            }
        }
        for (const m of template.cells.modifiers) {
            if (m && m.type === CellModifierType.Elevator && Array.isArray(m.cells) && m.cells.length >= 2) {
                const [a, b] = m.cells;
                drawElevator(ctx, Math.min(a.x, b.x) * cs, Math.min(a.y, b.y) * cs, (Math.abs(b.x - a.x) + 1) * cs, (Math.abs(b.y - a.y) + 1) * cs, cs);
            }
        }
        // Cells. Gate cells are skipped here and drawn afterwards as a single wide gate per head (so a
        // multi-cell gate is one stretched sprite, not one per cell).
        // A null entry is possible here for the same reason a modifier can be missing `cells`: the file is
        // read off Drive unchecked, the backend binds and skips nulls happily, and this draw runs from
        // componentDidUpdate — so a throw blanks the editor rather than reporting the bad file.
        // The boxes of a linked pair wear the game's connect cover on their lids (MarbleBox.WearConnectCover),
        // so the cells a Connected modifier names are collected before the cells draw.
        const connectedCells = new Set();
        for (const m of template.cells.modifiers) {
            if (m.type === CellModifierType.Connected && Array.isArray(m.cells)) {
                for (const mc of m.cells) {
                    if (mc)
                        connectedCells.add(`${mc.x},${mc.y}`);
                }
            }
        }
        for (const c of template.cells.items) {
            if (!c || c.isGate) {
                continue;
            }
            const key = `${c.x},${c.y}`;
            const preview = previewColors ? previewColors.get(key) : undefined;
            ctx.save();
            ctx.translate(c.x * cs, c.y * cs);
            if (!c.isPipe) {
                drawEditorCell(ctx, cs, c, preview !== undefined ? preview : null, connectedCells.has(key), previewSplits?.get(key) ?? null, previewInners?.get(key) ?? null);
            }
            ctx.restore();
        }
        // Wide gates: one stretched sprite per gate head, spanning gateLength cells.
        for (const c of template.cells.items) {
            if (c && c.isGate && c.gateLength > 0) {
                drawGateWide(ctx, c.x * cs, c.y * cs, c.gateLength * cs, cs);
            }
        }
        // Modifiers, drawn over the cells they cover. `cells` comes off Drive unchecked, so a hand-edited
        // modifier can be missing it entirely — `.length` on that would throw inside a draw called from
        // componentDidUpdate, i.e. a blank editor over one bad key.
        //
        // Laser gates are numbered in list order so each KEY can say which gate it opens.
        let laserNumber = 0;
        for (const m of template.cells.modifiers) {
            if (!m || !Array.isArray(m.cells) || m.cells.length < 1) {
                continue;
            }
            const a = m.cells[0];
            // Ice and the super mystery box name one box; the game reads cells[0] whatever else is listed.
            if (m.type === CellModifierType.FrozenBox) {
                withCoverAlpha(ctx, () => drawIceCover(ctx, a.x * cs, a.y * cs, cs, m.counter));
                continue;
            }
            if (m.type === CellModifierType.SuperMysteryBox) {
                drawSuperMysteryMark(ctx, a.x * cs, a.y * cs, cs);
                continue;
            }
            if (m.type === CellModifierType.MetalBox) {
                // A lone top-left cell implies the 2×2 (the game's NormalizeMetalBox).
                const c1 = m.cells[1] ?? { x: a.x + 1, y: a.y + 1 };
                const minx = Math.min(a.x, c1.x);
                const miny = Math.min(a.y, c1.y);
                withCoverAlpha(ctx, () => drawMetalBox(ctx, minx * cs, miny * cs, (Math.abs(c1.x - a.x) + 1) * cs, (Math.abs(c1.y - a.y) + 1) * cs, m.counter, cs));
                continue;
            }
            if (m.type === CellModifierType.GlassPanel) {
                const c1 = m.cells[1] ?? { x: a.x + 1, y: a.y + 1 };
                drawGlassPanel(ctx, Math.min(a.x, c1.x) * cs, Math.min(a.y, c1.y) * cs, (Math.abs(c1.x - a.x) + 1) * cs, (Math.abs(c1.y - a.y) + 1) * cs, glassPanelGoals(m));
                continue;
            }
            if (m.cells.length < 2) {
                continue;
            }
            const b = m.cells[1];
            if (m.type === CellModifierType.Elevator) {
                drawElevatorPatchSprite(ctx, Math.min(a.x, b.x) * cs, Math.min(a.y, b.y) * cs, (Math.abs(b.x - a.x) + 1) * cs, (Math.abs(b.y - a.y) + 1) * cs, cs);
                continue;
            }
            if (m.type === CellModifierType.Bolt) {
                if (a.x === b.x || a.y === b.y) {
                    withCoverAlpha(ctx, () => drawBolt(ctx, a, b, cs));
                }
                continue;
            }
            if (m.type === CellModifierType.SlidingDoor) {
                // Walked from the ANCHOR, so the housing lands on cells[0]. A diagonal names no cells (the
                // validator says why) and draws nothing.
                withCoverAlpha(ctx, () => drawSlidingDoor(ctx, runCells(a, b, BOARD_SIZE), m.counter, cs));
                continue;
            }
            if (m.type === CellModifierType.LaserGate) {
                laserNumber++;
                // An out-of-range pair (a hand-edited file) falls back to the first set rather than blanking the
                // editor on an undefined read — the validator is where that belongs.
                const pair = LASER_PAIR_COLORS[m.pairColor ?? 0] ? (m.pairColor ?? 0) : 0;
                const pairHex = LASER_PAIR_COLORS[pair].hex;
                if (a.x === b.x || a.y === b.y) {
                    drawLaserGate(ctx, a, b, cs, laserNumber, pairHex, pair);
                }
                if (m.cells[2]) {
                    drawLaserKey(ctx, m.cells[2], cs, laserNumber, pairHex, pair);
                }
                continue;
            }
            if (m.type === CellModifierType.Connected) {
                drawConnectorBar(ctx, (a.x + 0.5) * cs, (a.y + 0.5) * cs, (b.x + 0.5) * cs, (b.y + 0.5) * cs, cs * 0.28);
            }
            else if (m.type === CellModifierType.LockPanel) {
                const minx = Math.min(a.x, b.x);
                const miny = Math.min(a.y, b.y);
                const maxx = Math.max(a.x, b.x);
                const maxy = Math.max(a.y, b.y);
                withCoverAlpha(ctx, () => drawMetalRect(ctx, minx * cs, miny * cs, (maxx - minx + 1) * cs, (maxy - miny + 1) * cs, m.counter, cs));
            }
        }
        // Propeller clamps, over the boxes like a connector: each propeller to the cell it is tied to. Drawn even
        // when that cell is empty or a wall — the clamp pointing at nothing is how the author sees the Error.
        for (const c of template.cells.items) {
            if (!c || !c.isPropeller || c.isCleared) {
                continue;
            }
            const tied = propellerTiedCell(c);
            drawPropellerTie(ctx, (c.x + 0.5) * cs, (c.y + 0.5) * cs, (tied.x + 0.5) * cs, (tied.y + 0.5) * cs, cs);
        }
        // Badges, in their own pass AFTER the modifiers. They used to be drawn with each cell, which put the
        // super mystery cover, the metal box, the lock panel and the ice over them — a covered box's group,
        // the one thing the author most needs to read on it, was hidden under the very mechanic hiding the box.
        for (const c of template.cells.items) {
            if (!c || c.isGate) {
                continue;
            }
            const key = `${c.x},${c.y}`;
            ctx.save();
            ctx.translate(c.x * cs, c.y * cs);
            // Group badges. A box carries one, its own group, as a corner pip.
            //
            // A tunnel carries one per box it SPAWNS, in a strip across the top. Its own `group` is only the
            // fallback for a spawn with no record of its own (tunnelSpawnAt / LevelCompiler.TunnelSpawnFor) —
            // the boxes that come out of it carry the per-spawn trios, and the inspector doesn't even offer
            // the tunnel cell a Group field to set. So the single pip drawn here before showed the untouched
            // default 0 on every tunnel on the board, and the only way to see the groups that actually apply
            // was to select the tunnel and read the spawn rows one at a time.
            if (c.isTunnel) {
                // What the file claims, bounded by what the compiler will act on: spawns past the ceiling are
                // an error state the validator already reports, and drawing pips for them would promise the
                // author boxes that no colour is ever assigned to.
                const n = Math.min(Math.max(0, Math.floor(c.tunnelSpawnCount) || 0), MAX_TUNNEL_SPAWNS);
                for (const [i, slot] of spawnBadgeSlots(n, cs).entries()) {
                    drawGroupBadge(ctx, slot.cx, slot.cy, slot.r, tunnelSpawnAt(c, i).group);
                }
            }
            else if (spawnerOf(c)) {
                // Same strip, same reasons: a super tunnel's or a tool box's boxes are its spawns.
                const kind = spawnerOf(c);
                const n = Math.min(Math.max(0, Math.floor(spawnerCount(c, kind)) || 0), MAX_TUNNEL_SPAWNS);
                for (const [i, slot] of spawnBadgeSlots(n, cs).entries()) {
                    drawGroupBadge(ctx, slot.cx, slot.cy, slot.r, spawnerSpawnAt(c, kind, i).group);
                }
            }
            else if (!c.isCleared) {
                // A cleared cell carries no pip: it is not a box, so the compiler never reads its group.
                const r = Math.max(6, cs * 0.16);
                drawGroupBadge(ctx, r + cs * 0.02, r + cs * 0.02, r, c.group);
            }
            // The hand-painted mark: a small ring in the opposite corner from the group pip. Drawn on the
            // cell rather than only listed in the panel, because the board is where a designer looks to ask
            // "did I choose this colour, or did the seed?" — and re-rolling the seed keeps only the ringed
            // ones.
            if (paintedCells?.has(key)) {
                const r = Math.max(4, cs * 0.1);
                const cx = cs - r - cs * 0.06;
                const cy = cs - r - cs * 0.06;
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.lineWidth = Math.max(1.5, r * 0.4);
                ctx.strokeStyle = "rgba(255,255,255,0.95)";
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.lineWidth = Math.max(1, r * 0.16);
                ctx.strokeStyle = "rgba(0,0,0,0.45)";
                ctx.stroke();
            }
            ctx.restore();
        }
        // Pending modifier cell (first click of a two-click tool).
        const { pending } = this.props;
        if (pending) {
            ctx.save();
            ctx.setLineDash([cs * 0.14, cs * 0.1]);
            ctx.lineWidth = Math.max(2, cs * 0.06);
            ctx.strokeStyle = "#f59e0b";
            ctx.strokeRect(pending.x * cs + ctx.lineWidth / 2, pending.y * cs + ctx.lineWidth / 2, cs - ctx.lineWidth, cs - ctx.lineWidth);
            ctx.restore();
        }
        // The points a multi-click tool has picked so far, each with its word on it.
        for (const p of this.props.runPoints ?? []) {
            ctx.save();
            ctx.setLineDash([cs * 0.14, cs * 0.1]);
            ctx.lineWidth = Math.max(2, cs * 0.06);
            ctx.strokeStyle = "#f59e0b";
            ctx.strokeRect(p.x * cs + ctx.lineWidth / 2, p.y * cs + ctx.lineWidth / 2, cs - ctx.lineWidth, cs - ctx.lineWidth);
            ctx.setLineDash([]);
            ctx.font = `bold ${Math.max(9, Math.round(cs * 0.16))}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "top";
            ctx.fillStyle = "rgba(15,23,42,0.85)";
            const w = ctx.measureText(p.label).width + cs * 0.1;
            ctx.fillRect(p.x * cs + (cs - w) / 2, p.y * cs + cs * 0.06, w, cs * 0.2);
            ctx.fillStyle = "#fbbf24";
            ctx.fillText(p.label, p.x * cs + cs / 2, p.y * cs + cs * 0.08);
            ctx.restore();
        }
        // The selection: one tinted region with an outline round its edge only, so a box-selected block
        // reads as a single thing rather than a grid of little squares.
        const { selection, marquee } = this.props;
        if (selection && selection.size > 0) {
            ctx.save();
            ctx.fillStyle = "rgba(129,140,248,0.22)";
            for (const k of selection) {
                const [sx, sy] = k.split(",").map(Number);
                ctx.fillRect(sx * cs, sy * cs, cs, cs);
            }
            ctx.lineWidth = Math.max(2, cs * 0.05);
            ctx.strokeStyle = "#818cf8";
            ctx.lineCap = "square";
            ctx.beginPath();
            const h = ctx.lineWidth / 2;
            for (const k of selection) {
                const [sx, sy] = k.split(",").map(Number);
                const x0 = sx * cs;
                const y0 = sy * cs;
                if (!selection.has(`${sx},${sy - 1}`)) {
                    ctx.moveTo(x0 + h, y0 + h);
                    ctx.lineTo(x0 + cs - h, y0 + h);
                }
                if (!selection.has(`${sx},${sy + 1}`)) {
                    ctx.moveTo(x0 + h, y0 + cs - h);
                    ctx.lineTo(x0 + cs - h, y0 + cs - h);
                }
                if (!selection.has(`${sx - 1},${sy}`)) {
                    ctx.moveTo(x0 + h, y0 + h);
                    ctx.lineTo(x0 + h, y0 + cs - h);
                }
                if (!selection.has(`${sx + 1},${sy}`)) {
                    ctx.moveTo(x0 + cs - h, y0 + h);
                    ctx.lineTo(x0 + cs - h, y0 + cs - h);
                }
            }
            ctx.stroke();
            ctx.restore();
        }
        // The item the inspector is showing, inside a larger selection: a white ring marks which one it is.
        if (selected && selection && selection.size > 1) {
            ctx.save();
            ctx.lineWidth = Math.max(1.5, cs * 0.03);
            ctx.strokeStyle = "rgba(255,255,255,0.9)";
            const inset = cs * 0.08;
            ctx.strokeRect(selected.x * cs + inset, selected.y * cs + inset, cs - 2 * inset, cs - 2 * inset);
            ctx.restore();
        }
        else if (selected && !(selection && selection.size > 0)) {
            ctx.lineWidth = Math.max(2, cs * 0.06);
            ctx.strokeStyle = "#818cf8";
            ctx.strokeRect(selected.x * cs + ctx.lineWidth / 2, selected.y * cs + ctx.lineWidth / 2, cs - ctx.lineWidth, cs - ctx.lineWidth);
        }
        // The box-select rectangle being dragged out.
        if (marquee) {
            const x0 = Math.min(marquee.a.x, marquee.b.x) * cs;
            const y0 = Math.min(marquee.a.y, marquee.b.y) * cs;
            const x1 = (Math.max(marquee.a.x, marquee.b.x) + 1) * cs;
            const y1 = (Math.max(marquee.a.y, marquee.b.y) + 1) * cs;
            ctx.save();
            ctx.fillStyle = "rgba(129,140,248,0.12)";
            ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
            ctx.setLineDash([cs * 0.12, cs * 0.08]);
            ctx.lineWidth = Math.max(1.5, cs * 0.03);
            ctx.strokeStyle = "#c7d2fe";
            ctx.strokeRect(x0 + 1, y0 + 1, x1 - x0 - 2, y1 - y0 - 2);
            ctx.restore();
        }
    }
    // Ends the current gesture (drag or press) with no side effects.
    resetGesture() {
        this.pointerDownCell = null;
        this.pointerDownShift = false;
        this.gesture = null;
        this.lastPaintCell = null;
    }
    toCell(e) {
        const canvas = this.canvasRef.current;
        if (!canvas || this.cell <= 0) {
            return null;
        }
        const rect = canvas.getBoundingClientRect();
        const x = Math.floor((e.clientX - rect.left) / this.cell);
        const y = Math.floor((e.clientY - rect.top) / this.cell);
        if (x < 0 || y < 0 || x >= this.props.template.columns || y >= this.props.template.rows) {
            return null;
        }
        return { x, y };
    }
    // Tells the page the current gesture is over (no-op when there isn't one).
    endGesture() {
        if (this.gesture === "drag") {
            this.props.onCellDragEnd?.();
        }
        else if (this.gesture === "paint") {
            this.props.onCellPaintEnd?.();
        }
        else if (this.gesture === "marquee") {
            this.props.onMarqueeEnd?.();
        }
    }
    // The pointer says what a press would do: a move cursor over what a drag would move, a crosshair where
    // the Select tool (or ⇧) would start a box-select.
    updateCursor(cell, shift) {
        const canvas = this.canvasRef.current;
        if (!canvas) {
            return;
        }
        let cursor = "pointer";
        if (cell) {
            const inSelection = this.props.selection?.has(`${cell.x},${cell.y}`) ?? false;
            const item = findCell(this.props.template, cell.x, cell.y) !== undefined;
            const moves = this.props.selectMode ? inSelection : !this.props.dragPaints && item;
            if (shift || (this.props.selectMode && !moves)) {
                cursor = "crosshair";
            }
            else if (moves) {
                cursor = "move";
            }
        }
        canvas.style.cursor = cursor;
    }
    render() {
        return (_jsx("canvas", { ref: this.canvasRef, onMouseDown: this.onDown, onMouseUp: this.onUp, onContextMenu: this.onContext, onMouseMove: this.onMove, onMouseLeave: this.onLeave, className: "rounded-lg", style: { imageRendering: "auto" } }));
    }
}
